import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Style
import './LibrariesCompounds.scss';
import 'pages/Main/SectionHighlight.scss';

// Router
import { useNavigate, useParams } from 'react-router-dom';

// Classnames
import clsx from 'clsx';

// Lib
import { shallowEqual } from 'react-redux';
import { getValueSize } from 'lib/helpers';
import { useAppDispatch, useAppSelector } from 'lib/hooks/useDispatch';

// components
import List from './components/List';
import Header from './components/Header';
import Button from 'components/UiKit/Button';
import HeaderList from './components/HeaderList';
import ScrollButton from 'components/ScrollButton';
import ErrorBoundary from 'components/ErrorBoundary';
import PaginationView from 'components/PaginationView';
import PricesOnAsking from 'components/PricesOnAsking';
import DebounceInput from 'components/UiKit/DebounceInput';
import CustomMessage from 'components/Messages/CustomMessage';

// Actions
import * as actionsCreator from './reducer';
import { showMessage } from 'reducers/messages';

type TFuncArgs = {
	[index: string]: string | number;
}

// Constants
export const optionsSize = [
	{ label: '10', value: 10 },
	{ label: '20', value: 20 },
	{ label: '30', value: 30 },
	{ label: '40', value: 40 },
	{ label: '50', value: 50 },
	{ label: '60', value: 60 }
];
export const messageNotEmptyValue = {
	type: 'info',
	text: 'Value shouldn\'t be null'
};

export const LibrariesCompounds: React.FC = () => {
	const navigate = useNavigate();
	const urlParam = useParams();
	const dispatch = useAppDispatch();

	const {
		data,
		value,
		loading,
		dataSearch,
		toggleFilter,
		numberOfCompounds,

		name,
		compoundsCount,

		totalProgress,
		current
	} = useAppSelector(
		(store) => {
			return {
				data: store.librariesCompounds.data,
				value: store.librariesCompounds.value,
				loading: store.librariesCompounds.loading,
				dataSearch: store.librariesCompounds.dataSearch,
				toggleFilter: store.librariesCompounds.toggleFilter,

				numberOfCompounds: store.librariesCompounds.numberOfCompounds,

				name: store.librariesCompounds.name,
				compoundsCount: store.librariesCompounds.compoundsCount,

				totalProgress: store.librariesCompounds.progress.total,
				current: store.librariesCompounds.progress.current
			};
		},
		shallowEqual
	);

	const { id } = urlParam;
	const disabled = totalProgress === 0 || current === 0 || totalProgress !== current;
	const inputDebounce: HTMLInputElement | null = document.querySelector('.input-debounce__list');

	const [goTo, setGoTo] = useState<number>(1);
	const [isShow, setIsShow] = useState<boolean>(false);

	const {
		number,
		content
	} = data;

	useEffect(() => {
		return () => {
			dispatch(actionsCreator.handlerClearState());
		};
	}, []);

	useEffect(() => {
		if (id) {
			dispatch(actionsCreator.getLibrariesCompounds(id));
		}
	}, [id]);

	const onChangeInput = (
		// e: React.ChangeEvent<HTMLInputElement>
		value: string
	): void => {
		dispatch(actionsCreator.changeSearchInput(value));

		if (inputDebounce) {
			inputDebounce.style.display = 'block';
		}
	};

	const handlerSubmit = () => {
		if (!value?.length) {
			dispatch(showMessage(messageNotEmptyValue));
			return false;
		}

		dispatch(actionsCreator.searchLibrariesCompounds({
			value: value,
			cutNumber: id
		}));
		if (inputDebounce) {
			inputDebounce.style.display = 'none';
		}

		setIsShow(true);
	};

	const handlerClickLibrary = () => {
		navigate(`/libraries-product/${id}`);
	};

	const handleKeyDown = useCallback((
		e: React.KeyboardEvent<HTMLInputElement>
	): void | boolean => {
		if (e.key === 'Enter') {
			if (!value?.length) {
				dispatch(showMessage(messageNotEmptyValue));
				return false;
			}

			e.preventDefault();
			setIsShow(true);
			handlerSubmit();

			if (inputDebounce) {
				inputDebounce.style.display = 'none';
			}
		}
	}, [
		value,
		content,
		inputDebounce
	]);

	const leftIconAction = useCallback(() => {
		if (!value?.length) {
			dispatch(showMessage(messageNotEmptyValue));
			return false;
		}

		handlerSubmit();
		if (inputDebounce) {
			inputDebounce.style.display = 'none';
		}
	}, [
		value,
		content,
		inputDebounce
	]);

	const handlerClick = (value: string, step: number = 0) => {
		dispatch(actionsCreator.searchLibrariesCompounds({
			cutNumber: id,
			value: value,
			step: step
		}));
	};

	const handlerShowAll = () => {
		dispatch(actionsCreator.handleResetSearch());
		dispatch(actionsCreator.changeSearchInput(''));
		setIsShow(false);
	};

	const handlerChangeGoTo = (e: React.ChangeEvent<HTMLInputElement>) => {
		const number = +e?.target?.value > 0 ? +e?.target?.value : 1;

		if (number <= (total || 0)) {
			setGoTo(number);
		} else {
			dispatch(
				showMessage({
					type: 'info',
					text: 'This is the last record'
				})
			);
		}
	};

	const getRequestPagination = (number: number) => {
		dispatch(actionsCreator.handlerChangePage(number));
	};

	const handlerChangeSize = (data: TFuncArgs) => {
		dispatch(actionsCreator.handlerChangeSize(data?.value));
	};

	const onNextPressLast = () => {
		getRequestPagination((total || 0) - 1);
	};

	const onPrevPressLast = () => {
		getRequestPagination(0);
	};

	const onPagePress = (number: number) => {
		getRequestPagination(number - 1);
	};

	const onKeyPress = () => {
		dispatch(actionsCreator.handlerChangePage(goTo - 1));

		if (!!value) {
			handlerClick(value, goTo - 1);
		}
	};

	const result = isShow ? dataSearch?.content : content;
	const total = Math.ceil((result?.length || 0) / (numberOfCompounds || 20));

	const newData = useMemo(() => {
		if (result?.length) {
			const start = (numberOfCompounds || 20) * (number || 0);
			const end = start + (numberOfCompounds || 20);
			return result.slice(start, end);
		}
		return result;
	}, [result, number, numberOfCompounds]);

	const valueSize = getValueSize({
		opt: (numberOfCompounds || 20),
		options: optionsSize
	});

	const cnContent = clsx(
		'libraries-compounds__content mt30'
	);

	return (
		<ErrorBoundary>
			<ScrollButton />

			<div className="libraries-compounds relative">
				{toggleFilter && <div className="libraries-compounds__filter-mobile">
					<HeaderList
						isModal
						id={id || ''}
						className="libraries-compounds__filter-mobile-header"
					/>
				</div>}

				<div className="wrapper relative">
					<Header
						id={id || ''}
						name={name || ''}
						totalElements={compoundsCount || 0}
					/>

					<div className="libraries-compounds__search flex flex-ai-center flex-jc-between mb40">
						<div
							className="flex flex-ai-center libraries-compounds__search-wrapper flex-jc-start flex-direction-column-mobile">
							<DebounceInput
								disabled={disabled}

								isWhite
								isLeftIcon
								value={value}

								clickRecentItem={handlerClick}
								onChange={onChangeInput}
								onKeyDown={handleKeyDown}
								style={{ width: '34rem' }}
								suggest={dataSearch.content}
								leftIconAction={leftIconAction}
								placeholder="Virtid, ID, CAS, Index"
								classNameWrapper="input-debounce__white"
							/>

							<Button
								color="white"
								type="button"
								label="Search"
								variable="info"
								disabled={disabled}
								typePadding="middle"
								onPress={handlerSubmit}
								className="ml30 search-screen__search-btn"
							/>

							{isShow && <Button
								color="white"
								label="Show all"
								className="ml10"
								variable="outline"
								onPress={handlerShowAll}
							/>}
						</div>

						<Button
							color="white"
							variable="outline"
							label="More about library"
							onPress={handlerClickLibrary}
							className="libraries-compounds__more-about-library"
						/>
					</div>

					{((total || 0) > 1) && <PaginationView
						isWhite
						isShowSelect

						disabledGoTo={disabled}
						disabledSelect={disabled}
						disabledPagination={disabled}

						goTo={goTo}
						onKeyPress={onKeyPress}
						handlerSubmitGoTo={onKeyPress}
						handlerChangeGoTo={handlerChangeGoTo}

						number={number}
						totalPages={total}
						onPagePress={onPagePress}
						onNextPressLast={onNextPressLast}
						onPrevPressLast={onPrevPressLast}

						valueSize={valueSize}
						optionsSize={optionsSize}
						handlerChangeSize={handlerChangeSize}
					/>}

					<div className={cnContent}>
						<HeaderList id={id || ''} />

						<List
							loading={loading}
							data={newData || []}
						/>
					</div>

					{!!newData?.length && <div className="pt15 flex flex-ai-center flex-jc-between">
						{((total || 0) > 1) && <PaginationView
							isWhite
							className="full-width"

							children={<PricesOnAsking />}

							disabledGoTo={disabled}
							disabledSelect={disabled}
							disabledPagination={disabled}

							goTo={goTo}
							onKeyPress={onKeyPress}
							handlerSubmitGoTo={onKeyPress}
							handlerChangeGoTo={handlerChangeGoTo}

							number={number}
							totalPages={total}
							onPagePress={onPagePress}
							onNextPressLast={onNextPressLast}
							onPrevPressLast={onPrevPressLast}

							valueSize={valueSize}
							optionsSize={optionsSize}
							handlerChangeSize={handlerChangeSize}
						/>}
					</div>}

					{newData?.length
						? <CustomMessage
							title="Notice"
							className="mt30"
							text="Due to the dynamic change of the Restricted Product list, if the above material is included in the Restricted Product list or regulated as controlled material by the government, we will not sell it."
						/>
						: null}
				</div>
			</div>
		</ErrorBoundary>
	);
};

export default LibrariesCompounds;
